import type { LostReason } from '@/features/crm/deals/constants';
import { apiClient, type Paginated } from '@/core/api';
import type { AddLineInput, Proposal, ProposalInput } from './types';

export const BASE = '/crm/proposals/';

export interface ProposalListParams {
  page?: number;
  search?: string;
  status?: string;
  company?: number;
  deal?: number;
  /** Filter by proposal owner (salesperson) — a User id. */
  owner?: number;
  /** `true` → only archived rows; `'all'` → both. Unset/`false` → active only. */
  archived?: boolean | 'all';
  /** Created-date window (DateTime; `before` should be end-of-day). */
  created_after?: string;
  created_before?: string;
  /** DRF `OrderingFilter` — e.g. `created` or `-created`. */
  ordering?: string;
}

export async function listProposals(params: ProposalListParams = {}): Promise<Paginated<Proposal>> {
  const { data } = await apiClient.get<Paginated<Proposal>>(BASE, { params });
  return data;
}

export async function getProposal(id: string | number): Promise<Proposal> {
  const { data } = await apiClient.get<Proposal>(`${BASE}${id}/`);
  return data;
}

export async function createProposal(input: ProposalInput): Promise<Proposal> {
  const { data } = await apiClient.post<Proposal>(BASE, input);
  return data;
}

export async function updateProposal(id: string | number, input: Partial<ProposalInput>): Promise<Proposal> {
  const { data } = await apiClient.patch<Proposal>(`${BASE}${id}/`, input);
  return data;
}

/**
 * Soft-archive a proposal. The backend keeps the row, flips `is_archived=true`,
 * and hides it from default list queries. Use {@link restoreProposal} to reverse.
 */
export async function archiveProposal(id: string | number): Promise<void> {
  await apiClient.delete(`${BASE}${id}/`);
}

export async function restoreProposal(id: string | number): Promise<Proposal> {
  const { data } = await apiClient.post<Proposal>(`${BASE}${id}/restore/`);
  return data;
}

/** Permanently delete an archived proposal (hard delete; admin only, irreversible). */
export async function purgeProposal(id: string | number): Promise<void> {
  await apiClient.delete(`${BASE}${id}/purge/`);
}

/** POST /crm/proposals/{id}/add-line/ → returns the updated proposal. */
export async function addProposalLine(id: string | number, input: AddLineInput): Promise<Proposal> {
  const { data } = await apiClient.post<Proposal>(`${BASE}${id}/add-line/`, input);
  return data;
}

/** Accept the proposal → server creates a Project, wins the deal, audits all writes. */
export async function acceptProposal(
  id: string | number,
  input: { project_name?: string } = {},
): Promise<import('@/features/projects/types').Project> {
  const { data } = await apiClient.post(`${BASE}${id}/accept/`, input);
  return data as import('@/features/projects/types').Project;
}

/** Accept **without** provisioning a project — for work whose project already
 *  exists. Wins the deal and drafts a DRAFT contract; returns the proposal. */
export async function acceptProposalOnly(
  id: string | number,
  input: { project?: number } = {},
): Promise<Proposal> {
  const { data } = await apiClient.post<Proposal>(`${BASE}${id}/accept-only/`, input);
  return data;
}

/** Reject the proposal (close-lost) → marks it rejected, loses the linked deal,
 *  records the reason. Returns the updated proposal. */
export async function rejectProposal(
  id: string | number,
  /** `lost_reason` is required when this closes an open deal (ADR-108). */
  input: { reason?: string; lost_reason?: LostReason } = {},
): Promise<Proposal> {
  const { data } = await apiClient.post<Proposal>(`${BASE}${id}/reject/`, input);
  return data;
}

/** Draft a Contract from this proposal (spine step Proposal → Contract). Returns
 *  the new DRAFT contract; sign it from the Contract page to provision a Project. */
export async function createContractFromProposal(
  id: string | number,
  input: { title?: string } = {},
): Promise<import('@/features/crm/contracts/types').Contract> {
  const { data } = await apiClient.post(`${BASE}${id}/create-contract/`, input);
  return data as import('@/features/crm/contracts/types').Contract;
}
